import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "../../components/app-toast";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { APP_PATH } from "../../api/backendPath";
import { ACTIONS_UPDATE_ONLINE_DECISION } from "../../action-types/actions";
import { setOnsiteScheduleUpdated } from "../../slice/appointments/doctorAppointmentsSlice";

import i18n from "../../i18n";
/**
 * Doctor accepts / rejects an ONLINE (video) consultation request.
 *
 * The online counterpart of `updateOnsiteScheduleReducer`. An online booking already carries
 * the exact date/time the patient chose and has no transportation fee, so the payload is only
 * `{ action, rejection_reason? }`.
 *
 * Toast text comes from the API response, which is bilingual — the backend returns `message`
 * and `message_ar`, and the interceptor-facing errors carry the doctor-decision codes
 * (CONS_1230 "already decided", CONS_1229 "online only"), so the doctor sees the real reason
 * rather than a generic failure.
 */
export const updateOnlineDecisionReducer = createAsyncThunk(
  ACTIONS_UPDATE_ONLINE_DECISION.update_online_decision,
  async ({ appointmentId, payload, isArabic }, { rejectWithValue }) => {
    const axiosPrivate = await useAxiosPrivate();

    try {
      const response = await axiosPrivate.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.updateOnlineDecision}/${appointmentId}/update-online-decision`,
        payload,
      );

      if (response?.status === 200 || response?.data?.status === true) {
        const msg = isArabic
          ? response?.data?.message_ar || response?.data?.message
          : response?.data?.message;
        toast.success(
          msg ||
            (payload?.action === "accept"
              ? i18n.t("toast.consultationAccepted")
              : i18n.t("toast.consultationRejected")),
        );
        return response.data;
      }

      const errMsg = isArabic
        ? response?.data?.message_ar || response?.data?.message
        : response?.data?.message;
      toast.error(errMsg || i18n.t("toast.failedToUpdateTheConsultationDecision"));
      return rejectWithValue(response.data);
    } catch (error) {
      const data = error?.response?.data;
      const errMsg =
        (isArabic ? data?.message_ar || data?.message : data?.message) ||
        i18n.t("toast.errorUpdatingTheConsultationDecision");
      toast.error(errMsg);
      return rejectWithValue(errMsg);
    }
  },
);

export { setOnsiteScheduleUpdated };
